/**
 * Static content for the submission status side panel.
 *
 * Provides the "Steps to be Completed" checklist for each
 * {@link SubmissionDisplayState}. The "Steps Completed by Agent" tracker is no
 * longer sourced here; it is reconstructed from real RPA audit-trail data via
 * {@link useSubmissionAgentSteps} and passed into the side panel.
 *
 * @module submissionSidePanelData
 */
import { SubmissionDisplayState } from "./submissionDisplayState";

/** A single numbered item in the "Steps to be Completed" checklist. */
export interface SidePanelStep {
  id: string;
  text: string;
  hasPortalLink?: boolean;
}

/** Resolved static side panel content for a given display state. */
export interface SubmissionSidePanelData {
  stepsToBeCompleted: SidePanelStep[] | null;
}

/** Checklist shown after a successful portal/fax submission. */
const SUBMITTED_STEPS: SidePanelStep[] = [
  { id: "login", text: "Log in to payer portal", hasPortalLink: true },
  { id: "check", text: "Check on portal if submitted properly or not" },
  { id: "mark", text: "If submitted correctly marked as submitted" },
  { id: "report", text: "If not then report error" },
];

/** Checklist shown when a fax could not be delivered. */
const FAX_NOT_DELIVERED_STEPS: SidePanelStep[] = [
  { id: "login", text: "Log in to payer portal", hasPortalLink: true },
  { id: "report", text: "Report Error" },
];

/** Checklist shown when automated submission is not supported for the payer. */
const PAYER_NOT_SUPPORTED_STEPS: SidePanelStep[] = [
  { id: "login", text: "Log in to payer portal", hasPortalLink: true },
  { id: "submit", text: "Submit the authorization manually on the portal" },
  { id: "mark", text: "Once submitted, mark it as Submitted" },
];

/** Checklist shown when no filing attempt has been recorded yet. */
const NOT_SUBMITTED_STEPS: SidePanelStep[] = [
  { id: "login", text: "Log in to payer portal", hasPortalLink: true },
  {
    id: "submit",
    text: "Submit the authorization via automation or manually on the portal",
  },
  { id: "mark", text: "Once submitted, mark it as Submitted" },
];

/** Checklist shown when a submission attempt failed. */
const SUBMISSION_FAILED_STEPS: SidePanelStep[] = [
  { id: "login", text: "Log in to payer portal", hasPortalLink: true },
  {
    id: "check-cause",
    text: "Check the cause of the error. If it can be fixed by changing the information, update it and resubmit.",
  },
  {
    id: "mark",
    text: "If it goes through successfully, mark it as Submitted.",
  },
  {
    id: "report",
    text: "If the error still persists after your changes, Report Error.",
  },
];

/**
 * Returns the static side panel checklist for a display state.
 *
 * @input state - The resolved display state of the submission screen.
 * @returns The "Steps to be Completed" checklist, or null when none applies.
 */
export const getSubmissionSidePanelData = (
  state: SubmissionDisplayState,
): SubmissionSidePanelData => {
  switch (state) {
    case "automation_completed":
    case "submitted_successfully":
    case "fax_delivered":
      return { stepsToBeCompleted: SUBMITTED_STEPS };
    case "fax_not_delivered":
      return { stepsToBeCompleted: FAX_NOT_DELIVERED_STEPS };
    case "payer_not_supported":
      return { stepsToBeCompleted: PAYER_NOT_SUPPORTED_STEPS };
    case "not_submitted":
      return { stepsToBeCompleted: NOT_SUBMITTED_STEPS };
    case "submission_failed":
      return { stepsToBeCompleted: SUBMISSION_FAILED_STEPS };
    case "draft_saved":
    default:
      return { stepsToBeCompleted: null };
  }
};
